const collatorMap: { [locale: string]: Intl.Collator } = {};
// Safari 9 doesn't support Intl and the closest we can get is a localeCompare that uses system locale and can't be overriden.
const polyFilledCollator: Intl.Collator = {
    compare: String.prototype.localeCompare,
    resolvedOptions: undefined,
};
const getCollator = (locale: string) => {
    if (!collatorMap[locale]) {
        try {
            collatorMap[locale] = new Intl.Collator(locale, { sensitivity: 'base' });
        } catch {
            collatorMap[locale] = polyFilledCollator;
        }
    }

    return collatorMap[locale];
};

function compare<T extends boolean | number | string | Date>(
    left: T,
    right: T,
    locale: string
): number {
    if (left === undefined || left === null) {
        return left === right ? 0 : -1;
    }

    if (right === undefined || right === null) {
        return left === right ? 0 : 1;
    }

    // ##############################################################
    // Todo: Sorting with locales is hard, we need a good story on how to nail this. For now this is set to
    // something _other_ than english to be aware of the differences
    // Background: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/localeCompare)
    if (typeof left === 'string' && typeof right === 'string') {
        const collator = getCollator(locale);
        return collator.compare(left as string, right as string);
    }

    if (left < right) {
        return -1;
    }

    if (left > right) {
        return 1;
    }

    return 0;
}

export type FieldsExtractor<T> = (element: T) => (number | string | boolean | Date)[];
export type FieldExtractor<T> = (element: T) => number | string | boolean | Date;

/*
 * A general comparer function: Extracts the fields to sort on from the given objects, falls back on the
 * default field in case the field is undefined.
 */
export function sortBy<T>(
    locale: string,
    fieldExtractor: FieldsExtractor<T>,
    defaultField: FieldExtractor<T>,
    reverse?: boolean
): (left: T, right: T) => number {
    return (left: T, right: T): number => {
        const leftFields = fieldExtractor(left);
        const rightFields = fieldExtractor(right);

        for (let i = 0; i < leftFields.length; i++) {
            const leftValue = leftFields[i] !== undefined ? leftFields[i] : defaultField(left);
            const rightValue = rightFields[i] !== undefined ? rightFields[i] : defaultField(right);

            const comparisonResult = reverse
                ? compare(rightValue, leftValue, locale)
                : compare(leftValue, rightValue, locale);

            if (comparisonResult !== 0) {
                return comparisonResult;
            }
        }

        // All the fields are the same
        return 0;
    };
}
